import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { syntheticDemoPortalsEnabled } from '../../../shared/config/api-origin';
import type { ProductDraftErrors, ProductDraftFields } from '../domain/product-form';
import {
  cloneProductFields,
  productDemoCategories,
  productFieldDifferences,
  validateProductTemporaryFile,
} from '../domain/product-form';
import type { ProductDemoDraft } from '../fixtures/product-submission-demo';
import type { ProductEditorContext } from './ProductSubmissionPrototype';

/**
 * The confirmed 06 product editor.
 *
 * <p>TWO MODES, ONE EDITOR. `DEMO` is the original React-session draft editor and is unchanged.
 * `SYN_LIVE` drives the SAME dialog, fieldsets, validation, summary and footer against the durable
 * 06 contract, so the SYN path is the confirmed editor rather than a second form built beside it.
 *
 * <p>WHY THE LIVE MODE SHOWS FEWER FIELDS. The durable 06 contract carries exactly four claims —
 * model name, category, manufacturer claims and source. The editor's remaining fields (purpose,
 * features, limits, scenes, parameters, attachments, revision note) are NOT part of that contract,
 * so the LIVE mode renders them as an explicit "not carried" note instead of an input whose value
 * nothing would persist. Silently dropping typed text, or pretending to save it, are both worse
 * than saying which fields the contract does not have.
 */
export function ProductModelEditor({
  context,
  records,
  onClose,
  onSave,
  mode = 'DEMO',
  live = null,
  busy = false,
  contentEditor,
}: {
  context: ProductEditorContext;
  records: readonly ProductDemoDraft[];
  onClose: () => void;
  onSave: (fields: ProductDraftFields, submitting: boolean) => ProductDraftErrors;
  mode?: 'DEMO' | 'SYN_LIVE';
  live?: {
    readonly productId: string;
    readonly version: number | null;
    readonly state: string | null;
    readonly digest: string | null;
  } | null;
  busy?: boolean;
  contentEditor?: ReactNode;
}) {
  const liveMode = mode === 'SYN_LIVE';
  const [fields, setFields] = useState(() => cloneProductFields(context.fields));
  const [errors, setErrors] = useState<ProductDraftErrors>({});
  const [fileError, setFileError] = useState('');
  const dialog = useRef<HTMLDialogElement>(null);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (element && !element.open) element.showModal();
    return () => {
      if (element?.open) element.close();
    };
  }, []);
  const change = (key: Exclude<keyof ProductDraftFields, 'parameters' | 'files'>, value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const input = (
    key: Exclude<keyof ProductDraftFields, 'parameters' | 'files'>,
    label: string,
    max: number,
    multiline = false,
    hint = '',
  ) => (
    <label className="product-demo-field" key={key}>
      <span>
        {label}
        <small>{max} 字内</small>
      </span>
      {multiline ? (
        <textarea
          value={fields[key]}
          maxLength={max + 1}
          aria-invalid={Boolean(errors[key])}
          aria-describedby={`product-error-${key}`}
          onChange={(e) => change(key, e.target.value)}
          rows={3}
        />
      ) : (
        <input
          value={fields[key]}
          maxLength={max + 1}
          aria-invalid={Boolean(errors[key])}
          aria-describedby={`product-error-${key}`}
          onChange={(e) => change(key, e.target.value)}
        />
      )}
      {hint && <small>{hint}</small>}
      <span id={`product-error-${key}`} className="product-demo-field-error">
        {errors[key]}
      </span>
    </label>
  );
  const save = (submitting: boolean) => {
    if (fileError) {
      setErrors({ files: fileError });
      return;
    }
    const result = onSave(fields, submitting);
    setErrors(result);
    if (Object.keys(result).length)
      requestAnimationFrame(() =>
        form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
      );
  };
  const notCarried = (names: readonly string[]) => (
    <p className="product-demo-help">
      06 持久化契约未承载：{names.join('、')}。本编辑器不接收这些字段，以免出现“保存成功但未落库”。
    </p>
  );
  if (!liveMode && !syntheticDemoPortalsEnabled()) return null;
  return (
    <dialog
      ref={dialog}
      className="product-demo-dialog"
      aria-labelledby="product-editor-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header className="product-demo-dialog-header">
        <div>
          <h2 id="product-editor-title">
            {liveMode
              ? context.baseline
                ? '修订产品资料（SYN 数据库）'
                : '新增产品（SYN 数据库）'
              : context.baseline
                ? '修订产品资料（演示）'
                : records.some((r) => r.demoProductDraftId === context.id)
                  ? '编辑会话草稿（演示）'
                  : '新增产品（演示）'}
          </h2>
          <p>
            {liveMode
              ? 'SYN_LIVE · 非生产合成测试 · 结果由 06 Owner 写入数据库'
              : 'DEMO_ONLY · Supplier 身份与 Scope 未接入 · 仅 React 会话内存'}
          </p>
        </div>
        <button
          type="button"
          className="product-demo-close"
          aria-label="关闭产品编辑器"
          onClick={onClose}
        >
          ×
        </button>
      </header>
      <form
        ref={form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
      >
        <div className="product-demo-editor-layout">
          <div className="product-demo-editor-fields">
            <fieldset>
              <legend>基础信息</legend>
              {liveMode
                ? notCarried(['厂商显示标识（服务端合成身份决定）', '独立产品型号字段'])
                : input(
                    'manufacturer',
                    '厂商显示标识（仅演示）',
                    80,
                    false,
                    'UNKNOWN / 未接入可信身份来源',
                  )}
              {input('name', liveMode ? '产品名称与型号 *' : '产品名称 *', 80)}
              {!liveMode &&
                input('model', '产品型号', 60, false, 'UNKNOWN 可保存部分草稿；模拟提交须明确型号')}
              <label className="product-demo-field">
                <span>{liveMode ? '产品类别 *' : '产品类别（模拟提交必填）'}</span>
                <select
                  value={fields.category}
                  aria-invalid={Boolean(errors.category)}
                  onChange={(e) => change('category', e.target.value)}
                >
                  <option value="">请选择类别</option>
                  {productDemoCategories.map((category) => (
                    <option key={category}>{category}</option>
                  ))}
                </select>
                <span className="product-demo-field-error">{errors.category}</span>
              </label>
              {liveMode ? null : input('purpose', '用途简介（厂家声明）', 600, true)}
            </fieldset>
            <fieldset>
              <legend>技术说明 · 未经独立验证</legend>
              {liveMode ? (
                <>
                  {input('claims', '厂家能力声明（不是 VERIFIED）*', 1000, true)}
                  {notCarried(['核心特点', '正式场景适用关系'])}
                </>
              ) : (
                <>
                  {input('features', '核心特点', 1000, true)}
                  {input('claims', '厂家能力声明（不是 VERIFIED）', 1000, true)}
                  {input('limits', '安全与技术限制（模拟提交必填）', 1000, true)}
                  {input('scenes', '典型场景（仅厂家声明）', 600, true)}
                </>
              )}
              {!liveMode && (
                <>
                  <div className="product-demo-parameter-head">
                    <h3>技术参数</h3>
                    <button
                      type="button"
                      className="portal-button"
                      disabled={fields.parameters.length >= 30}
                      onClick={() =>
                        setFields((previous) => ({
                          ...previous,
                          parameters: [...previous.parameters, { key: '', value: '', unit: '' }],
                        }))
                      }
                    >
                      添加参数
                    </button>
                  </div>
                  <p className="product-demo-help">
                    名称和值必填；参数不得重名，单位可选。最多 30 项。
                  </p>
                  {fields.parameters.map((parameter, index) => (
                    <div className="product-demo-parameter" key={index}>
                      {(['key', 'value', 'unit'] as const).map((key) => (
                        <label key={key}>
                          <span>
                            {key === 'key' ? '参数名称' : key === 'value' ? '参数值' : '单位'}
                          </span>
                          <input
                            aria-label={`${key === 'key' ? '参数名称' : key === 'value' ? '参数值' : '单位'} ${index + 1}`}
                            value={parameter[key]}
                            maxLength={key === 'key' ? 61 : key === 'value' ? 161 : 31}
                            aria-invalid={Boolean(errors[`parameter-${index}`])}
                            onChange={(e) =>
                              setFields((previous) => ({
                                ...previous,
                                parameters: previous.parameters.map((item, number) =>
                                  number === index ? { ...item, [key]: e.target.value } : item,
                                ),
                              }))
                            }
                          />
                        </label>
                      ))}
                      <button
                        type="button"
                        aria-label={`移除参数 ${index + 1}`}
                        onClick={() =>
                          setFields((previous) => ({
                            ...previous,
                            parameters: previous.parameters.filter((_, number) => number !== index),
                          }))
                        }
                      >
                        移除
                      </button>
                      <span className="product-demo-field-error">
                        {errors[`parameter-${index}`]}
                      </span>
                    </div>
                  ))}
                  {errors.parameters && (
                    <p className="product-demo-field-error">{errors.parameters}</p>
                  )}
                </>
              )}
            </fieldset>
            <fieldset>
              <legend>资料与来源</legend>
              {input(
                'source',
                liveMode ? '资料来源 *' : '资料来源（模拟提交必填）',
                600,
                true,
                '来源只是未核实声明，不构成版权、资质或能力审批。',
              )}
              {liveMode
                ? notCarried(['修订说明（修订原因由版本与审核意见承载）', '本地临时附件名'])
                : input(
                    'reason',
                    '修订说明',
                    600,
                    true,
                    context.baseline
                      ? '拟议 v2 模拟提交前必须说明修改原因。'
                      : '新资料可说明用途与来源。',
                  )}
              {!liveMode && (
                <>
                  <label className="product-demo-field">
                    <span>本地临时附件名（不读取或上传文件）</span>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.pdf"
                      multiple
                      onChange={(e) => {
                        const selected = Array.from(e.target.files ?? []).map((file) => ({
                          name: file.name,
                          type: file.type,
                          size: file.size,
                        }));
                        const candidate = [...fields.files, ...selected];
                        const issue =
                          selected
                            .map(validateProductTemporaryFile)
                            .find((error) => error !== null) ??
                          (candidate.length > 8
                            ? '最多选择 8 个临时附件名'
                            : new Set(candidate.map((file) => file.name.toLowerCase())).size !==
                                candidate.length
                              ? '临时文件名重复'
                              : '');
                        setFileError(issue);
                        if (!issue) setFields((previous) => ({ ...previous, files: candidate }));
                        e.target.value = '';
                      }}
                    />
                    <small>
                      PNG / JPEG / PDF · 单件 5 MB 内 · 最多 8 件。仅检查文件名、扩展名、浏览器 MIME
                      和大小；内容、签名与权利未知。无预览、无文件读取、无 objectURL。
                    </small>
                  </label>
                  {(fileError || errors.files) && (
                    <p role="alert" className="product-demo-field-error">
                      {fileError || errors.files}
                      <button
                        type="button"
                        onClick={() => {
                          setFileError('');
                          setErrors((previous) => ({ ...previous, files: '' }));
                        }}
                      >
                        清除选择错误
                      </button>
                    </p>
                  )}
                  {fields.files.length ? (
                    <ul className="product-demo-files">
                      {fields.files.map((file, index) => (
                        <li key={`${file.name}-${index}`}>
                          <span>
                            {file.name} · {Math.ceil(file.size / 1024)} KB · 待上传
                          </span>
                          <button
                            type="button"
                            aria-label={`移除附件 ${file.name}`}
                            onClick={() =>
                              setFields((previous) => ({
                                ...previous,
                                files: previous.files.filter((_, number) => number !== index),
                              }))
                            }
                          >
                            移除
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="product-demo-file-placeholder">
                      产品图片 / 产品手册 / 证明文件：待上传 · 本轮无正式上传服务
                    </p>
                  )}
                </>
              )}
            </fieldset>
          </div>
          <aside className="product-demo-editor-summary">
            <h3>资料摘要</h3>
            <dl>
              <div>
                <dt>{liveMode ? '产品编号' : '草稿标识'}</dt>
                <dd>{liveMode ? (live?.productId ?? context.id) : context.id}</dd>
              </div>
              <div>
                <dt>{liveMode ? 'CAS 版本' : '演示版本'}</dt>
                <dd>
                  {liveMode
                    ? (live?.version ?? '新建')
                    : context.baseline
                      ? '拟议 v2 / v1 只读'
                      : '新建 v1 草稿'}
                </dd>
              </div>
              {liveMode && (
                <div>
                  <dt>服务端状态</dt>
                  <dd>{live?.state ?? '尚未创建'}</dd>
                </div>
              )}
              <div>
                <dt>资料来源</dt>
                <dd>{fields.source || '未知 · 待补充'}</dd>
              </div>
              {liveMode ? (
                <div>
                  <dt>产品类别</dt>
                  <dd>{fields.category || '未选择'}</dd>
                </div>
              ) : (
                <div>
                  <dt>型号</dt>
                  <dd>{fields.model || 'UNKNOWN'}</dd>
                </div>
              )}
              {liveMode ? (
                <div>
                  <dt>当前摘要</dt>
                  <dd style={{ wordBreak: 'break-all' }}>{live?.digest ?? '尚无已保存摘要'}</dd>
                </div>
              ) : (
                <div>
                  <dt>技术参数</dt>
                  <dd>{fields.parameters.length} 项 · 未核实</dd>
                </div>
              )}
            </dl>
            <p>
              {liveMode
                ? '保存与提交都由 06 Owner 在数据库中确认；页面不显示未经服务端确认的成功。'
                : '保存草稿允许部分字段未知；模拟提交须具备名称、明确型号、类别、用途、厂家声明、限制与来源。'}
            </p>
            {!liveMode && (
              <>
                <h3>v1 / v2 字段比较</h3>
                <ul className="product-demo-mini-diff">
                  {productFieldDifferences(context.baseline, fields).map((change) => (
                    <li key={change.label}>
                      <strong>{change.label}</strong>
                      <del>{change.before}</del>
                      <span>{change.after}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p>
              Manufacturer Claim ≠ Verified Capability。本端不审批、不发布、不关联 05 资产、不向 08
              同步。
            </p>
          </aside>
        </div>
        {Object.keys(errors).length > 0 && (
          <div className="product-demo-errors" role="alert">
            <strong>请检查资料</strong>
            {Object.entries(errors)
              .filter(([, value]) => value)
              .map(([field, value]) => (
                <p key={field}>{value}</p>
              ))}
          </div>
        )}
        {liveMode && contentEditor}
        <footer className="product-demo-editor-footer">
          <button type="button" className="portal-button product-demo-secondary" onClick={onClose}>
            取消并返回
          </button>
          <button type="submit" className="portal-button" disabled={busy}>
            {liveMode ? '保存到数据库' : '保存本次会话草稿（演示）'}
          </button>
          <button
            type="button"
            className="portal-button"
            disabled={busy}
            onClick={() => save(true)}
          >
            {liveMode ? '保存并提交审核' : '模拟提交审核（不发送到08）'}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
